
/* ==========================================
   TIMI FONT PREVIEW v1.5

   Unified font picker for:
   - Hats: Choose a Font
   - Bag charms: Font Style
   - Future Zepto font thumbnail fields

   Fixes:
   - Incorrect comma-separated CSS selectors
   - Outer cranberry border
   - Side-by-side font names on hats
   - Inconsistent tile spacing

   Preserves Zepto selection values and pricing.
   ========================================== */

(() => {
  'use strict';

  if (window.__timiFontPreviewV15) return;
  window.__timiFontPreviewV15 = true;

  // One selector expression prevents the comma
  // expansion issue in the previous version.
  const WRAPPER = [
    '.product-personalizer',
    ':is(.pplrfontthumb,',
    '.pplr-choose-a-font,',
    '.pplr-font-style)'
  ].join(' ');

  const ITEM = '.pplr-selecter-item';
  const GRID = '.pplr-selecter-options';
  const STYLE_ID = 'timi-font-preview-styles';

  function injectStyles() {
    let style = document.getElementById(STYLE_ID);

    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }

    style.textContent = `
      /* Only style the actual font grid */
      ${WRAPPER} ${GRID} {
        display: grid !important;
        grid-template-columns:
          repeat(3, minmax(0, 1fr)) !important;
        gap: 7px !important;
        padding: 2px 5px 6px 2px !important;
        box-sizing: border-box !important;
      }

      /* Individual font tiles */
      ${WRAPPER} ${GRID} ${ITEM} {
        display: flex !important;
        flex-direction: column !important;
        flex-wrap: nowrap !important;
        align-items: center !important;
        justify-content: center !important;

        gap: 3px !important;
        width: 100% !important;
        min-width: 0 !important;
        height: auto !important;
        min-height: 54px !important;

        padding: 7px 4px !important;
        margin: 0 !important;
        box-sizing: border-box !important;

        background: #ffffff !important;
        border: 1px solid #e8dfe2 !important;
        border-radius: 8px !important;
        box-shadow: none !important;

        text-align: center !important;
        line-height: normal !important;
        cursor: pointer !important;
      }

      ${WRAPPER} ${GRID} ${ITEM}:hover {
        background: #fff7fa !important;
        border-color: #f7bcd0 !important;
      }

      /* Only highlight the selected tile */
      ${WRAPPER} ${GRID} ${ITEM}.selected,
      ${WRAPPER} ${GRID} ${ITEM}.selected-font {
        background: #fff1f5 !important;
        border: 2px solid #8f182f !important;
      }

      /* Abc gets its own centered line */
      ${WRAPPER} .timi-font-preview-sample {
        display: block !important;
        position: static !important;
        float: none !important;
        clear: both !important;
        flex: 0 0 auto !important;

        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;

        margin: 0 !important;
        padding: 0 !important;

        font-size: 21px !important;
        line-height: 1.12 !important;
        text-align: center !important;
        white-space: nowrap !important;

        color: #282428 !important;
        overflow: hidden !important;
      }

      /* Font name always underneath Abc */
      ${WRAPPER} .timi-font-preview-name {
        display: block !important;
        position: static !important;
        float: none !important;
        flex: 0 0 auto !important;

        width: 100% !important;
        max-width: 100% !important;

        margin: 0 !important;
        padding: 0 !important;

        font-family:
          -apple-system,
          BlinkMacSystemFont,
          "Segoe UI",
          Arial,
          sans-serif !important;

        font-size: 10px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        letter-spacing: normal !important;
        line-height: 1.2 !important;

        color: #72666a !important;
        text-align: center !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;
      }

      ${WRAPPER} ${ITEM}.selected
      .timi-font-preview-name,
      ${WRAPPER} ${ITEM}.selected-font
      .timi-font-preview-name {
        color: #8f182f !important;
        font-weight: 600 !important;
      }

      ${WRAPPER} ${ITEM}:focus-visible {
        outline: 2px solid #3e5e92 !important;
        outline-offset: 2px !important;
      }

      /* Soft scrollbar */
      ${WRAPPER} ::-webkit-scrollbar {
        width: 5px;
      }

      ${WRAPPER} ::-webkit-scrollbar-thumb {
        background: #decbd1;
        border-radius: 8px;
      }

      /* Mobile */
      @media (max-width: 749px) {
        ${WRAPPER} ${GRID} {
          gap: 6px !important;
        }

        ${WRAPPER} ${GRID} ${ITEM} {
          min-height: 51px !important;
          padding: 6px 3px !important;
        }

        ${WRAPPER} .timi-font-preview-sample {
          font-size: 19px !important;
        }

        ${WRAPPER} .timi-font-preview-name {
          font-size: 9px !important;
        }
      }
    `;
  }

  function getFontName(option) {
    const existing = option.querySelector(
      '.timi-font-preview-name'
    );

    return String(
      option.getAttribute('data-name') ||
      option.dataset.timiFontName ||
      existing?.textContent ||
      option.getAttribute('data-value') ||
      option.textContent ||
      ''
    ).trim();
  }

  function getFontAppearance(option) {
    const originalChild = [
      ...option.children
    ].find(child =>
      !child.classList.contains(
        'timi-font-preview-sample'
      ) &&
      !child.classList.contains(
        'timi-font-preview-name'
      )
    );

    const source = originalChild || option;
    const computed = getComputedStyle(source);

    return {
      family: computed.fontFamily,
      weight: computed.fontWeight,
      style: computed.fontStyle,
      spacing: computed.letterSpacing
    };
  }

  function buildTile(option) {
    const sample = option.querySelector(
      '.timi-font-preview-sample'
    );

    const name = option.querySelector(
      '.timi-font-preview-name'
    );

    // Already enhanced: retain the font applied
    // when Zepto originally rendered the option.
    if (
      option.dataset.timiFontEnhanced === 'true' &&
      sample &&
      name
    ) {
      return;
    }

    const fontName = getFontName(option);
    if (!fontName) return;

    const appearance = getFontAppearance(option);

    const newSample = document.createElement('span');
    newSample.className = 'timi-font-preview-sample';
    newSample.textContent = 'Abc';

    if (appearance.family) {
      newSample.style.fontFamily = appearance.family;
    }

    if (appearance.weight) {
      newSample.style.fontWeight = appearance.weight;
    }

    if (appearance.style) {
      newSample.style.fontStyle = appearance.style;
    }

    if (appearance.spacing) {
      newSample.style.letterSpacing = appearance.spacing;
    }

    const newName = document.createElement('span');
    newName.className = 'timi-font-preview-name';
    newName.textContent = fontName;

    // Retain the real Zepto option element, including
    // its data attributes and selection handlers.
    option.replaceChildren(newSample, newName);

    option.dataset.timiFontEnhanced = 'true';
    option.dataset.timiFontName = fontName;

    option.setAttribute('title', fontName);
    option.setAttribute('aria-label', fontName);
  }

  function enhance() {
    document.querySelectorAll(WRAPPER)
      .forEach(wrapper => {
        wrapper.querySelectorAll(
          `${GRID} ${ITEM}`
        ).forEach(buildTile);
      });
  }

  function init() {
    injectStyles();
    enhance();

    let scheduled = false;

    const observer = new MutationObserver(records => {
      const relevant = records.some(record => {
        if (record.type !== 'childList') return false;

        const target = record.target;

        return (
          !!target.closest?.(WRAPPER) ||
          [...record.addedNodes].some(node =>
            node.nodeType === 1 &&
            (
              node.matches?.(WRAPPER) ||
              node.querySelector?.(WRAPPER)
            )
          )
        );
      });

      if (!relevant || scheduled) return;

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        enhance();
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    window.addEventListener('pageshow', enhance);

    setTimeout(enhance, 500);
    setTimeout(enhance, 1500);

    console.log('TIMI Font Preview v1.5 initialized');
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
